import { __decorate, __metadata, __param } from "tslib";
import { E_NoticeConfigurationOpenMode, E_NotificationMsgReadWay } from '@/apis/service/noticeCenter/NoticeCenterApi.type';
import { Symbols } from '@/symbols';
import { getBooleanOptions } from '@/utils/options';
import { inject, injectable } from 'inversify';
import _ from 'lodash';
let NoticeConfigurationService = class NoticeConfigurationService {
    constructor(noticeCenterApi, productService, accountApi, systemService, i18n) {
        this.noticeCenterApi = noticeCenterApi;
        this.productService = productService;
        this.accountApi = accountApi;
        this.systemService = systemService;
        this.i18n = i18n;
        this.postNotificationScenesPagination = this.noticeCenterApi.postNotificationScenesPagination;
        this.postNotificationUserMessagePagination = this.noticeCenterApi.postNotificationUserMessagePagination;
        this.getNotificationSceneTypes = this.noticeCenterApi.getNotificationSceneTypes;
        this.postNotificationSceneTypes = this.noticeCenterApi.postNotificationSceneTypes;
        this.putNotificationSceneTypes = this.noticeCenterApi.putNotificationSceneTypes;
        this.sceneTypeOptions = [];
        this.productOptions = [];
    }
    getNickName(item) {
        if (this.systemService.IsCn) {
            return item.nickname;
        }
        return _.trim(`${item.firstname || ''} ${item.lastname || ''}`);
    }
    getAccounts(account) {
        return this.accountApi.getListAccounts({
            s_identifiers: account,
            sEcho: 1,
            iDisplayLength: 20,
            iDisplayStart: 0,
        }).then(res => {
            return res.aaData.map((el) => {
                let accountStr = el.email || _.toString(el.mobile_number) || this.getNickName(el) || el.user_id;
                if (_.includes(el.email, account)) {
                    accountStr = el.email;
                }
                else if (_.includes(_.toString(el.mobile_number), account)) {
                    accountStr = _.toString(el.mobile_number);
                }
                else if (_.includes(this.getNickName(el), account)) {
                    accountStr = this.getNickName(el);
                }
                return {
                    user_id: el.user_id,
                    account: accountStr,
                };
            });
        });
    }
    saveNotificationScenes(params) {
        if (params.id) {
            return this.noticeCenterApi.putNotificationScenes(params);
        }
        return this.noticeCenterApi.postNotificationScenes(params);
    }
    getNotificationScenes(id) {
        return this.noticeCenterApi.getNotificationScenes(id);
    }
    makeNoticeConfigurationFormData(params) {
        return _.merge({
            product_id: '',
            template_cn: '',
            template_tw: '',
            template_us: '',
            template_jp: '',
            link: '',
            open_mode: null,
            popup: null,
            enabled: null,
            description: '',
            scene_key: '',
        }, params);
    }
    getEnabledOptions() {
        return getBooleanOptions(this.i18n.t('backoffice.common.enabled_of_true'), this.i18n.t('backoffice.common.enabled_of_false'));
    }
    getNeedLinkOptions() {
        return getBooleanOptions(this.i18n.t('navigation_library.common.yes'), this.i18n.t('navigation_library.common.no'));
    }
    getPopupOptions() {
        return getBooleanOptions(this.i18n.t('navigation_library.common.yes'), this.i18n.t('navigation_library.common.no'));
    }
    getOpenModeOptions() {
        return [
            { text: this.i18n.t('backoffice.notifications.newTab'), value: E_NoticeConfigurationOpenMode.NewTab },
            { text: this.i18n.t('backoffice.notifications.currentPage'), value: E_NoticeConfigurationOpenMode.CurrentTab }
        ];
    }
    getSceneTypeOptions() {
        const options = _.filter(this.sceneTypeOptions, item => item.enabled);
        return _.map(options, item => {
            return {
                value: item.id,
                label: this.i18n.locale === 'cn' ? item.scene_type_name_cn : item.scene_type_name_us,
            };
        });
    }
    getSceneTypeOptionsLabel(id) {
        const find = _.find(this.sceneTypeOptions, { id });
        return this.i18n.locale === 'cn' ? find.scene_type_name_cn : find.scene_type_name_us;
    }
    getSceneTypeLabel(item) {
        return this.i18n.locale === 'cn' ? item.scene_type_name_cn : item.scene_type_name_us;
    }
    getOpenModeValue(item) {
        if (item.open_mode === E_NoticeConfigurationOpenMode.NewTab) {
            return this.i18n.t('backoffice.notifications.newTab');
        }
        if (item.open_mode === E_NoticeConfigurationOpenMode.CurrentTab) {
            return this.i18n.t('backoffice.notifications.currentPage');
        }
        return '-';
    }
    getSceneTypeValue(item) {
        return this.i18n.locale === 'cn' ? item.scene_type_name_cn : item.scene_type_name_us;
    }
    async getProductOptions() {
        await this.productService.getProductionProducts();
        this.productOptions = [
            ...this.productService.packageProductsOnSale,
            {
                product_id: 'backoffice',
                product_desc: {
                    en_US: 'Backoffice',
                    zh_CN: 'Backoffice',
                },
                product_name: 'Backoffice',
            }
        ];
    }
    getPackageProductById(productId) {
        return this.productOptions.find(it => it.product_id === productId);
    }
    getProductLabel(options) {
        let item = options;
        if (typeof options === 'string') {
            item = this.getPackageProductById(options);
        }
        return _.get(item, `alias_${this.i18n.locale}`, '') || _.get(item, 'product_name', '');
    }
    getReadStatusOptions() {
        return getBooleanOptions(this.i18n.t('backoffice.notifications.read'), this.i18n.t('backoffice.notifications.unread'));
    }
    getReadStatus(item) {
        if (item.read_at) {
            return this.i18n.t('backoffice.notifications.read');
        }
        return this.i18n.t('backoffice.notifications.unread');
    }
    getReadWayOptions() {
        return [
            { text: this.i18n.t('backoffice.notifications.batch'), value: E_NotificationMsgReadWay.batch },
            { text: this.i18n.t('backoffice.notifications.single'), value: E_NotificationMsgReadWay.single }
        ];
    }
    getReadWay(item) {
        const findRead = _.find(this.getReadWayOptions(), { value: item.read_way });
        return findRead ? findRead.text : '-';
    }
    getClickedOptions() {
        return getBooleanOptions(this.i18n.t('backoffice.notifications.clicked'), this.i18n.t('backoffice.notifications.unclicked'));
    }
    getClicked(item) {
        if (item.clicked) {
            return this.i18n.t('backoffice.notifications.clicked');
        }
        return this.i18n.t('backoffice.notifications.unclicked');
    }
    async getNoticeConfigurationTypeData() {
        this.sceneTypeOptions = await this.getNotificationSceneTypes();
        return this.sceneTypeOptions;
    }
    postNotificationTypeData(params) {
        return this.postNotificationSceneTypes(params);
    }
    putNotificationTypeData(params) {
        return this.putNotificationSceneTypes(params);
    }
};
NoticeConfigurationService = __decorate([
    injectable(),
    __param(0, inject(Symbols.NoticeCenterApi)),
    __param(1, inject(Symbols.ProductService)),
    __param(2, inject(Symbols.AccountApi)),
    __param(3, inject(Symbols.SystemService)),
    __param(4, inject(Symbols.I18n)),
    __metadata("design:paramtypes", [Object, Object, Object, Object, Object])
], NoticeConfigurationService);
export { NoticeConfigurationService };
